import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Search,
  RefreshCw,
  Pencil,
  Trash2,
  X,
  Save,
  ArrowRight,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
  AlertTriangle,
  Loader2,
  Ban,
  ExternalLink
} from 'lucide-react';
import { MARKET_COUNTRIES } from '../../services/marketCountries';
import { PAID_CHANNEL_LIST, channelMeta, labelHref } from '../../services/paidChannels';
import AdCreativeViewer from '../../services/AdCreativeViewer';

const basePath = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

const PAGE_SIZES = [25, 50, 100];

const SOURCES = [
  { value: '', label: 'All sources' },
  { value: 'manual', label: 'Manual' },
  { value: 'generated', label: 'Generated' },
  { value: 'removal', label: 'Removals' }
];

const SOURCE_STYLES = {
  manual: 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-400',
  generated: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300',
  removal: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300'
};

const fmt = (n) => (n === null || n === undefined || n === '' ? '—' : Number(n).toLocaleString());

const fmtDate = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

/** Type-the-word confirmation. Different words for different destructive actions
 *  so muscle memory from one cannot fire the other. */
const ConfirmTypeModal = ({ title, word, body, busy, error, onCancel, onConfirm }) => {
  const [typed, setTyped] = useState('');
  const ready = typed === word;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-slate-700">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-red-600 dark:text-red-400">
            <AlertTriangle className="h-5 w-5" />
            {title}
          </h2>
          <button type="button" onClick={onCancel} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          <div className="text-sm text-gray-600 dark:text-slate-300">{body}</div>
          <div>
            <label className="mb-1 block text-sm text-gray-600 dark:text-slate-300">
              Type <span className="font-mono font-semibold text-red-600 dark:text-red-400">{word}</span> to confirm
            </label>
            <input
              type="text"
              value={typed}
              autoFocus
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && ready && onConfirm()}
              placeholder={word}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 font-mono text-sm text-gray-900 outline-none focus:border-red-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
            />
          </div>
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800/50 dark:bg-red-900/20 dark:text-red-300">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4 dark:border-slate-700">
          <button type="button" onClick={onCancel} className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800">
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!ready || busy}
            className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            {busy ? 'Working…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Every paid-traffic entry ever recorded, across all affiliates.
 *
 * Two distinct destructive actions, deliberately kept apart:
 *  - Delete entry  — corrects a mistaken record. The affiliate's paid traffic
 *                    number is untouched; only the keyword this entry introduced
 *                    is retracted.
 *  - Remove from paid traffic — the affiliate stopped advertising. Zeroes its
 *                    paid traffic, strips the keywords this team added, and logs
 *                    a removal entry. Past entries are kept.
 */
const PaidTrafficLog = () => {
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [source, setSource] = useState('');
  const [channel, setChannel] = useState('');

  const [editEntry, setEditEntry] = useState(null);
  const [editForm, setEditForm] = useState({ keyword: '', country: '', new_traffic: '' });
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const [deleteEntry, setDeleteEntry] = useState(null);
  const [removeEntry, setRemoveEntry] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmError, setConfirmError] = useState('');

  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [searchInput]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (search) params.set('search', search);
      if (source) params.set('source', source);
      if (channel) params.set('channel', channel);

      const res = await fetch(`${basePath}/api/paid-traffic/history?${params.toString()}`, {
        credentials: 'include'
      });

      if (res.status === 403) {
        setRows([]);
        setPagination(null);
        setError('Access denied — this page is available to the admin account only.');
        return;
      }

      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || `Server error: ${res.status}`);

      setRows(data.data || []);
      setPagination(data.pagination || null);
    } catch (err) {
      setError(err.message || 'Failed to load the paid traffic log');
      setRows([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, source, channel]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(''), 5000);
    return () => clearTimeout(t);
  }, [notice]);

  const openEdit = (entry) => {
    setEditEntry(entry);
    setEditError('');
    setEditForm({
      keyword: entry.keyword || '',
      advertiser_name: entry.advertiser_name || '',
      platforms: entry.platforms || '',
      ad_library_id: entry.ad_library_id || '',
      country: entry.country || '',
      new_traffic: entry.new_traffic ?? ''
    });
  };

  const saveEdit = async () => {
    setSaving(true);
    setEditError('');
    try {
      // Only send the half that applies to this entry's channel. Sending the
      // other half would blank fields the operator was never shown.
      const isSocial = editEntry.channel && editEntry.channel !== 'google';
      const payload = isSocial
        ? {
            advertiser_name: editForm.advertiser_name,
            platforms: editForm.platforms,
            ad_library_id: editForm.ad_library_id,
            country: editForm.country,
            new_traffic: editForm.new_traffic
          }
        : {
            keyword: editForm.keyword,
            country: editForm.country,
            new_traffic: editForm.new_traffic
          };

      const res = await fetch(`${basePath}/api/paid-traffic/history/${editEntry.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || `Server error: ${res.status}`);

      setNotice(data.message);
      setEditEntry(null);
      fetchData();
    } catch (err) {
      setEditError(err.message || 'Failed to update entry');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    setBusy(true);
    setConfirmError('');
    try {
      const res = await fetch(`${basePath}/api/paid-traffic/history/${deleteEntry.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || `Server error: ${res.status}`);

      setNotice(data.message);
      setDeleteEntry(null);
      fetchData();
    } catch (err) {
      setConfirmError(err.message || 'Failed to delete entry');
    } finally {
      setBusy(false);
    }
  };

  const confirmRemove = async () => {
    setBusy(true);
    setConfirmError('');
    try {
      const res = await fetch(`${basePath}/api/paid-traffic/${removeEntry.affiliate_id}/remove-paid-traffic`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || `Server error: ${res.status}`);

      setNotice(
        `${data.message}. ${data.keywordsStripped} keyword(s) removed, ${data.keywordsKept} kept.`
      );
      setRemoveEntry(null);
      fetchData();
    } catch (err) {
      setConfirmError(err.message || 'Failed to remove paid traffic');
    } finally {
      setBusy(false);
    }
  };

  const totalPages = pagination?.totalPages || 1;

  return (
    <div className="mx-auto max-w-[1600px]">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-slate-100">Paid Traffic Log</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
          Every paid-traffic entry ever recorded. Correct a mistaken entry, or take an affiliate off paid traffic when
          it stops running ads.
        </p>
      </div>

      {notice && (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800 dark:border-emerald-800/50 dark:bg-emerald-900/20 dark:text-emerald-300">
          {notice}
        </div>
      )}

      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search affiliate, keyword, advertiser or country…"
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-9 text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          />
          {searchInput && (
            <button type="button" onClick={() => setSearchInput('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <select
          value={source}
          onChange={(e) => {
            setSource(e.target.value);
            setPage(1);
          }}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
        >
          {SOURCES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>

        <select
          value={channel}
          onChange={(e) => {
            setChannel(e.target.value);
            setPage(1);
          }}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
        >
          <option value="">All channels</option>
          {PAID_CHANNEL_LIST.map((c) => (
            <option key={c.key} value={c.key}>{c.label}</option>
          ))}
        </select>

        <select
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(1);
          }}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
        >
          {PAGE_SIZES.map((s) => (
            <option key={s} value={s}>{s} / page</option>
          ))}
        </select>

        <button
          type="button"
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800/50 dark:bg-red-900/20 dark:text-red-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-900">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px] text-left text-sm">
            <thead className="border-b border-gray-200 bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-600 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300">
              <tr>
                <th className="px-4 py-3">Affiliate</th>
                <th className="w-40 px-4 py-3">Change</th>
                <th className="w-24 px-4 py-3">Channel</th>
                {/* A Google entry records a keyword, a social one an advertiser
                    Page. One column, because they answer the same question. */}
                <th className="px-4 py-3">Found as</th>
                <th className="w-40 px-4 py-3">Country</th>
                <th className="w-28 px-4 py-3">Source</th>
                <th className="w-48 px-4 py-3">When</th>
                <th className="w-24 px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
              {loading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-16 text-center text-gray-500 dark:text-slate-400">
                    <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin" />
                    Loading log…
                  </td>
                </tr>
              )}

              {!loading && rows.length === 0 && !error && (
                <tr>
                  <td colSpan={8} className="px-4 py-16 text-center text-gray-500 dark:text-slate-400">
                    Nothing recorded yet. Entries appear here once you set paid traffic on an affiliate.
                  </td>
                </tr>
              )}

              {rows.map((entry) => (
                <tr key={entry.id} className="align-top hover:bg-gray-50/70 dark:hover:bg-slate-800/40">
                  <td className="px-4 py-3">
                    <a
                      href={`https://${entry.affiliate_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 font-medium text-blue-600 hover:underline dark:text-blue-400"
                    >
                      {entry.affiliate_url}
                      <ExternalLink className="h-3 w-3 flex-shrink-0 opacity-60" />
                    </a>
                    {!entry.affiliate_exists && (
                      <div className="mt-0.5 text-[11px] text-gray-400 dark:text-slate-500">
                        affiliate no longer in the table
                      </div>
                    )}
                  </td>

                  <td className="px-4 py-3 tabular-nums text-gray-700 dark:text-slate-300">
                    <span className="inline-flex items-center gap-1">
                      {fmt(entry.previous_traffic)}
                      <ArrowRight className="h-3 w-3 text-gray-400" />
                      <span className={entry.source === 'removal' ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}>
                        {fmt(entry.new_traffic)}
                      </span>
                    </span>
                    {entry.affiliate_exists && entry.new_traffic !== entry.current_traffic && (
                      <div className="mt-0.5 text-[11px] text-gray-400 dark:text-slate-500">
                        now {fmt(entry.current_traffic)}
                      </div>
                    )}
                  </td>

                  <td className="px-4 py-3">
                    {(() => {
                      const entryChannel = channelMeta(entry.channel);
                      if (!entryChannel) return <span className="text-gray-300 dark:text-slate-600">—</span>;
                      return (
                        <span
                          className="inline-flex items-center gap-1.5"
                          title={entryChannel.where}
                        >
                          <span className={`inline-flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-bold ${entryChannel.solid}`}>
                            {entryChannel.letter}
                          </span>
                          <span className="text-xs text-gray-600 dark:text-slate-400">{entryChannel.short}</span>
                        </span>
                      );
                    })()}
                  </td>

                  <td className="px-4 py-3">
                    {entry.advertiser_name ? (
                      <div>
                        {labelHref(entry.advertiser_name) ? (
                          <a
                            href={labelHref(entry.advertiser_name)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block max-w-[220px] truncate rounded bg-indigo-50 px-1.5 py-0.5 text-xs text-indigo-700 hover:underline dark:bg-indigo-900/20 dark:text-indigo-300"
                            title={entry.advertiser_name}
                          >
                            {entry.advertiser_name}
                          </a>
                        ) : (
                          <span className="inline-block max-w-[220px] truncate rounded bg-indigo-50 px-1.5 py-0.5 text-xs text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300" title={entry.advertiser_name}>
                            {entry.advertiser_name}
                          </span>
                        )}
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-gray-400 dark:text-slate-500">
                          {entry.platforms && <span className="truncate">{entry.platforms}</span>}
                          <AdCreativeViewer images={entry.ad_images} label={entry.advertiser_name} compact />
                        </div>
                      </div>
                    ) : entry.keyword ? (
                      <span className="inline-block max-w-[220px] truncate rounded bg-blue-50 px-1.5 py-0.5 text-xs text-blue-700 dark:bg-blue-900/20 dark:text-blue-300" title={entry.keyword}>
                        {entry.keyword}
                      </span>
                    ) : (
                      <span className="text-gray-300 dark:text-slate-600">—</span>
                    )}
                  </td>

                  <td className="px-4 py-3 text-gray-700 dark:text-slate-300">
                    {entry.country || <span className="text-gray-300 dark:text-slate-600">—</span>}
                  </td>

                  <td className="px-4 py-3">
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${SOURCE_STYLES[entry.source] || SOURCE_STYLES.manual}`}>
                      {entry.source}
                    </span>
                  </td>

                  <td className="px-4 py-3 text-xs text-gray-500 dark:text-slate-400">
                    <div>{fmtDate(entry.created_at)}</div>
                    {entry.created_by && <div className="truncate" title={entry.created_by}>{entry.created_by}</div>}
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {entry.source !== 'removal' && (
                        <button
                          type="button"
                          onClick={() => openEdit(entry)}
                          title="Correct this entry"
                          className="rounded-lg p-1.5 text-gray-500 hover:bg-blue-50 hover:text-blue-600 dark:text-slate-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                      {entry.affiliate_exists && Number(entry.current_traffic) > 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            setRemoveEntry(entry);
                            setConfirmError('');
                          }}
                          title="Affiliate stopped running ads — remove it from paid traffic"
                          className="rounded-lg p-1.5 text-gray-500 hover:bg-orange-50 hover:text-orange-600 dark:text-slate-400 dark:hover:bg-orange-900/20 dark:hover:text-orange-400"
                        >
                          <Ban className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setDeleteEntry(entry);
                          setConfirmError('');
                        }}
                        title="Delete this log entry"
                        className="rounded-lg p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600 dark:text-slate-400 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-4 py-3 text-sm dark:border-slate-700">
          <span className="text-gray-500 dark:text-slate-400">
            {pagination?.total ? `${pagination.total.toLocaleString()} entries` : 'No entries'}
          </span>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setPage(1)} disabled={page <= 1 || loading} className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-40 dark:text-slate-400 dark:hover:bg-slate-800">
              <ChevronsLeft className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading} className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-40 dark:text-slate-400 dark:hover:bg-slate-800">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-3 text-gray-600 dark:text-slate-300">
              Page {page.toLocaleString()} of {totalPages.toLocaleString()}
            </span>
            <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading} className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-40 dark:text-slate-400 dark:hover:bg-slate-800">
              <ChevronRight className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => setPage(totalPages)} disabled={page >= totalPages || loading} className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-40 dark:text-slate-400 dark:hover:bg-slate-800">
              <ChevronsRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Edit entry */}
      {editEntry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white shadow-xl dark:bg-slate-900">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-slate-700">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">Correct entry</h2>
                <p className="mt-0.5 font-mono text-xs text-gray-500 dark:text-slate-400">{editEntry.affiliate_url}</p>
              </div>
              <button type="button" onClick={() => setEditEntry(null)} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 px-5 py-4">
              {/* A social entry has no keyword to correct — it is identified by
                  the Page that ran the ad. */}
              {editEntry.channel && editEntry.channel !== 'google' ? (
                <>
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">Advertiser (Page)</label>
                    <input
                      type="text"
                      value={editForm.advertiser_name}
                      onChange={(e) => setEditForm((f) => ({ ...f, advertiser_name: e.target.value }))}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">Platforms</label>
                      <input
                        type="text"
                        value={editForm.platforms}
                        onChange={(e) => setEditForm((f) => ({ ...f, platforms: e.target.value }))}
                        placeholder="Facebook,Instagram"
                        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">Library ID</label>
                      <input
                        type="text"
                        value={editForm.ad_library_id}
                        onChange={(e) => setEditForm((f) => ({ ...f, ad_library_id: e.target.value }))}
                        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 font-mono text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div>
                  <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">Keyword</label>
                  <input
                    type="text"
                    value={editForm.keyword}
                    onChange={(e) => setEditForm((f) => ({ ...f, keyword: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                  />
                  <p className="mt-1 text-xs text-gray-400 dark:text-slate-500">
                    If this entry added the keyword to the affiliate, correcting it here corrects it there too.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">Country</label>
                  <select
                    value={editForm.country}
                    onChange={(e) => setEditForm((f) => ({ ...f, country: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                  >
                    <option value="">No country</option>
                    {MARKET_COUNTRIES.map((c) => (
                      <option key={c.code} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">Recorded traffic</label>
                  <input
                    type="number"
                    min={0}
                    value={editForm.new_traffic}
                    onChange={(e) => setEditForm((f) => ({ ...f, new_traffic: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                  />
                  <p className="mt-1 text-xs text-gray-400 dark:text-slate-500">
                    Corrects the log only — the affiliate's live number is unchanged.
                  </p>
                </div>
              </div>

              {editError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800/50 dark:bg-red-900/20 dark:text-red-300">
                  {editError}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4 dark:border-slate-700">
              <button type="button" onClick={() => setEditEntry(null)} className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800">
                Cancel
              </button>
              <button
                type="button"
                onClick={saveEdit}
                disabled={saving}
                className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteEntry && (
        <ConfirmTypeModal
          title="Delete log entry"
          word="DELETE"
          busy={busy}
          error={confirmError}
          onCancel={() => setDeleteEntry(null)}
          onConfirm={confirmDelete}
          body={
            <>
              <p>
                Deletes this log entry for{' '}
                <span className="font-semibold text-gray-900 dark:text-slate-100">{deleteEntry.affiliate_url}</span>.
              </p>
              <p className="mt-2">
                The affiliate's paid traffic number stays as it is. If this entry added the keyword
                {deleteEntry.keyword ? <span className="font-medium"> "{deleteEntry.keyword}"</span> : null} to the
                affiliate, that keyword is retracted.
              </p>
              <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">
                To take the affiliate off paid traffic entirely, use the Remove action instead.
              </p>
            </>
          }
        />
      )}

      {removeEntry && (
        <ConfirmTypeModal
          title="Remove from paid traffic"
          word="REMOVE"
          busy={busy}
          error={confirmError}
          onCancel={() => setRemoveEntry(null)}
          onConfirm={confirmRemove}
          body={
            <>
              <p>
                <span className="font-semibold text-gray-900 dark:text-slate-100">{removeEntry.affiliate_url}</span> has
                stopped running paid ads.
              </p>
              <p className="mt-2">
                Its paid traffic goes to 0 and the keywords your team added are stripped, so the Paid tag disappears
                from Affiliate Recruitment, Affiliate Directory and Untapped Opportunities. Keywords SEMrush reported
                are kept.
              </p>
              <p className="mt-2">The past entries stay, plus a new removal entry recording this.</p>
            </>
          }
        />
      )}
    </div>
  );
};

export default PaidTrafficLog;
